import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Award, BookOpen, Clock, Download, Eye, GraduationCap, AlertCircle } from 'lucide-react';
import { CoursePlayer } from './LMSPage';

const API = process.env.REACT_APP_BACKEND_URL;

const sessionKey = (token) => `lms_guest_session_${token}`;

const readSession = (token) => {
  try { return localStorage.getItem(sessionKey(token)); } catch (e) { return null; }
};
const writeSession = (token, value) => {
  try {
    if (value) localStorage.setItem(sessionKey(token), value);
    else localStorage.removeItem(sessionKey(token));
  } catch (e) { /* noop */ }
};

// Página pública: a pessoa informa nome completo (e-mail opcional), faz o curso
// e recebe o certificado em seu nome — sem precisar de usuário no sistema.
const PublicCoursePage = () => {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | invalid | form | course | done
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [session, setSession] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', cpf: '', code: '' });
  const [codeSentTo, setCodeSentTo] = useState(null); // e-mail mascarado após pedir o código
  const [submitting, setSubmitting] = useState(false);
  const [certificate, setCertificate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      // Retoma sessão salva neste navegador (recarregar a página não perde o progresso)
      const saved = readSession(token);
      if (saved) {
        try {
          const me = await axios.get(`${API}/api/lms-guest/${saved}/me`);
          if (cancelled) return;
          setSession(saved);
          setForm((f) => ({ ...f, name: me.data.name }));
          setInfo({ course: { id: me.data.course_id } });
          if (me.data.completed) {
            setCertificate(me.data);
            setPhase('done');
          } else {
            setPhase('course');
          }
          return;
        } catch (e) {
          writeSession(token, null);
        }
      }
      try {
        const res = await axios.get(`${API}/api/lms-public/${token}`);
        if (cancelled) return;
        setInfo(res.data);
        setPhase('form');
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Link inválido');
        setPhase('invalid');
      }
    };
    init();
    return () => { cancelled = true; };
  }, [token]);

  // Pede o código de acesso: é enviado ao e-mail do cadastro do funcionário
  const requestCode = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/lms-public/${token}/request-code`, { cpf: form.cpf });
      setCodeSentTo(res.data.email_masked);
      setForm((f) => ({ ...f, code: '' }));
      toast.success('Código enviado para o seu e-mail');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível enviar o código');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStart = async (e) => {
    e.preventDefault();
    const name = form.name.trim().replace(/\s+/g, ' ');
    const byCpf = info?.identify_by === 'cpf';
    if (byCpf && form.cpf.replace(/\D/g, '').length !== 11) {
      toast.error('Informe um CPF válido (11 dígitos)');
      return;
    }
    if (!byCpf && !info?.employee_name && name.split(' ').length < 2) {
      toast.error('Informe seu nome completo (nome e sobrenome)');
      return;
    }
    if (byCpf && !codeSentTo) {
      await requestCode();
      return;
    }
    if (byCpf && form.code.replace(/\D/g, '').length !== 6) {
      toast.error('Informe o código de 6 dígitos enviado ao seu e-mail');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/lms-public/${token}/start`, {
        name,
        email: form.email.trim() || null,
        cpf: byCpf ? form.cpf : null,
        code: byCpf ? form.code : null,
      });
      writeSession(token, res.data.session);
      setSession(res.data.session);
      setForm((f) => ({ ...f, name: res.data.name }));
      // Funcionário que já concluiu: vai direto ao certificado
      const me = await axios.get(`${API}/api/lms-guest/${res.data.session}/me`);
      if (me.data.completed) {
        setCertificate(me.data);
        setPhase('done');
        return;
      }
      setInfo((prev) => ({ ...(prev || {}), course: { ...(prev?.course || {}), id: res.data.course_id } }));
      setPhase('course');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível iniciar o curso');
    } finally {
      setSubmitting(false);
    }
  };

  const openCertificate = (path) => window.open(`${API}${path}`, '_blank', 'noopener');

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-3">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
            <h1 className="text-lg font-semibold">Não foi possível abrir o curso</h1>
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500">Solicite um novo link a quem enviou este.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'form') {
    const course = info?.course || {};
    const employeeName = info?.employee_name;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <GraduationCap className="h-5 w-5" />
              <span className="text-sm font-medium">Curso online com certificado</span>
            </div>
            <CardTitle className="text-2xl">{course.title}</CardTitle>
            {course.description && <CardDescription>{course.description}</CardDescription>}
            <div className="flex gap-4 text-sm text-gray-600 pt-2">
              <span className="flex items-center gap-1"><BookOpen className="h-4 w-4" />{course.lessons_count} aula(s)</span>
              {course.duration_hours > 0 && (
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{course.duration_hours}h</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleStart} className="space-y-4">
              {info?.identify_by === 'cpf' ? (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="guest_cpf">CPF *</Label>
                    <Input
                      id="guest_cpf"
                      inputMode="numeric"
                      value={form.cpf}
                      onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      disabled={!!codeSentTo}
                      required
                    />
                  </div>
                  {codeSentTo ? (
                    <div className="space-y-2">
                      <div className="rounded-lg border bg-blue-50 p-3 text-sm">
                        Enviamos um código de acesso para <strong>{codeSentTo}</strong>. Ele vale por 15 minutos.
                      </div>
                      <Label htmlFor="guest_code">Código de acesso *</Label>
                      <Input
                        id="guest_code"
                        inputMode="numeric"
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                        required
                      />
                      <div className="flex justify-between text-xs">
                        <button type="button" className="text-blue-600 underline" disabled={submitting} onClick={requestCode}>
                          Reenviar código
                        </button>
                        <button type="button" className="text-gray-500 underline" onClick={() => { setCodeSentTo(null); setForm({ ...form, code: '' }); }}>
                          Trocar CPF
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Vamos localizar seu cadastro de funcionário pelo CPF e enviar um código de acesso para o e-mail cadastrado. O certificado será emitido no nome do cadastro.
                    </p>
                  )}
                </div>
              ) : employeeName ? (
                <div className="rounded-lg border bg-blue-50 p-4 text-sm">
                  <p className="text-gray-600">Este link é de:</p>
                  <p className="text-lg font-semibold">{employeeName}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    O certificado será emitido neste nome. Não é você? Peça o seu próprio link.
                  </p>
                </div>
              ) : (<>
              <div>
                <Label htmlFor="guest_name">Nome completo *</Label>
                <Input
                  id="guest_name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Como deve aparecer no certificado"
                  maxLength={120}
                  required
                />
              </div>
              <div>
                <Label htmlFor="guest_email">E-mail (opcional)</Label>
                <Input
                  id="guest_email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="seu@email.com"
                  maxLength={200}
                />
              </div>
              <p className="text-xs text-gray-500">
                Confira a grafia do nome: o certificado será emitido exatamente como digitado.
              </p>
              </>)}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Aguarde...' : (info?.identify_by === 'cpf' && !codeSentTo ? 'Enviar código para meu e-mail' : 'Iniciar curso')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <Award className="h-14 w-14 text-yellow-500 mx-auto" />
            <h1 className="text-xl font-bold">Parabéns{form.name ? `, ${form.name.split(' ')[0]}` : ''}!</h1>
            <p className="text-gray-600">Você concluiu o curso. Seu certificado está pronto.</p>
            {certificate?.certificate_number && (
              <p className="text-xs text-gray-500 font-mono">{certificate.certificate_number}</p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={() => openCertificate(certificate.download_path)}>
                <Download className="h-4 w-4 mr-2" />
                Baixar certificado (PDF)
              </Button>
              <Button variant="outline" onClick={() => openCertificate(certificate.view_path)}>
                <Eye className="h-4 w-4 mr-2" />
                Visualizar certificado
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Guarde este link: você pode voltar a ele neste navegador para baixar o certificado novamente.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // phase === 'course'
  return (
    <CoursePlayer
      courseId={info?.course?.id}
      guest={{ session }}
      onClose={() => {}}
      onGuestComplete={(data) => {
        setCertificate(data);
        setPhase('done');
      }}
    />
  );
};

export default PublicCoursePage;
